import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import i18n from "i18next"
import { v4 as uuidv4 } from "uuid"

import { WorkflowConstantFeature } from "../../../../../types/generated/api/WorkflowConstantFeature"
import type { ConstantFeature } from "../../../types/interfaces"

/**
 * Type casts a value based on the specified workflow constant feature type.
 * Handles conversion for NUMBER, BOOLEAN, LIST, and STRING types.
 *
 * @param value - The value to type cast
 * @param type - The target type for casting
 * @returns The type-cast value
 */
export const typeCastBasedOnType = (value: unknown, type: WorkflowConstantFeature.type): unknown => {
  switch (type) {
    case WorkflowConstantFeature.type.NUMBER:
      return isNaN(Number(value)) ? 0 : Number(value)
    case WorkflowConstantFeature.type.BOOLEAN:
      // Convert value to string, make it lowercase, and compare with "true"
      // This handles various boolean string representations ("True", "TRUE", "true")
      // Returns true only if the lowercase value exactly matches "true", false otherwise
      return String(value).toLowerCase() === "true"
    case WorkflowConstantFeature.type.LIST:
      if (Array.isArray(value)) {
        return value.length === 0 ? [] : value
      } else {
        return value ? [value] : []
      }
    // case "DICT":
    //   if (value && typeof value === "object") {
    //     return Object.keys(value).length === 0 ? {} : value
    //   } else {
    //     return {}
    //   }
    default:
      return `${value}`
  }
}

// Lightweight validation that only checks JSON structure and basic rules
/**
 * Validates the JSON structure for workflow constants.
 * Ensures the JSON is parseable and has the correct structure.
 *
 * @param jsonText - The JSON string to validate
 * @returns void if valid, Error if invalid
 */
export const validateJsonStructure = (jsonText: string): void | Error => {
  if (!jsonText.trim()) {
    return // Empty JSON is valid
  }

  const parsed = JSON.parse(jsonText)

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error(i18n.t("workflowSchemaDialog.constants.notifications.JSONTypeError", { ns: "workflows" }))
  }

  // Quick validation of values
  for (const [key, value] of Object.entries(parsed)) {
    if (value === null || value === undefined) {
      throw new Error(i18n.t("workflowSchemaDialog.constants.notifications.nullKeyError", { key, ns: "workflows" }))
    }
    if (typeof value === "object" && !Array.isArray(value)) {
      throw new Error(i18n.t("workflowSchemaDialog.constants.notifications.objectKeyError", { key, ns: "workflows" }))
    }
  }

  return
}

// Full conversion function (optimized version of your original)
/**
 * Converts JSON text to table data format for workflow constants.
 * Merges with existing constants and generates unique IDs.
 *
 * @param jsonText - The JSON string to convert
 * @param existingConstants - Existing constants to merge with
 * @returns Array of constant features in table format
 */
export const convertJsonToTableData = (jsonText: string, existingConstants: ConstantFeature[]): ConstantFeature[] => {
  const jsonConstants = JSON.parse(jsonText)

  // Helper function to infer type from value
  const inferType = (value: unknown): WorkflowConstantFeature.type => {
    switch (typeof value) {
      case "boolean":
        return WorkflowConstantFeature.type.BOOLEAN
      case "number":
        return WorkflowConstantFeature.type.NUMBER
      case "string":
        return WorkflowConstantFeature.type.TEXT
      default:
        return Array.isArray(value) ? WorkflowConstantFeature.type.LIST : WorkflowConstantFeature.type.TEXT
    }
  }

  // Create a lookup map for O(1) JSON constant access
  const jsonConstantsMap = new Map<string, unknown>()
  for (const [key, value] of Object.entries(jsonConstants)) {
    jsonConstantsMap.set(key, value)
  }

  // Process existing constants more efficiently
  const processedConstants: ConstantFeature[] = []

  for (const constant of existingConstants) {
    if (jsonConstantsMap.has(constant.name)) {
      const jsonValue = jsonConstantsMap.get(constant.name)
      jsonConstantsMap.delete(constant.name) // Mark as processed

      if (constant.is_used) {
        processedConstants.push(constant) // Keep used constants unchanged
      } else {
        processedConstants.push({
          ...constant,
          value: jsonValue,
          type: inferType(jsonValue),
        }) // Update non-used constants
      }
    } else {
      if (constant.is_used) {
        processedConstants.push(constant) // Keep used constants even if not in JSON
      }
      // Unused and not in JSON are skipped
    }
  }

  // Add new constants from JSON (remaining entries in the map)
  const newConstants = Array.from(jsonConstantsMap.entries()).map(([key, value]) => ({
    id: uuidv4(),
    name: key,
    value,
    type: inferType(value),
    is_used: false,
    new: true,
  }))

  return [...newConstants, ...processedConstants]
}

/**
 * Tests JSON validity and shows appropriate error messages.
 *
 * @param jsonText - The JSON string to test
 * @param fallbackMessage - Fallback message for generic errors
 * @returns true if valid, false if invalid
 */
export const testJson = (jsonText: string, fallbackMessage: string): boolean => {
  try {
    validateJsonStructure(jsonText)
    return true
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))

    NotificationUtils.toast(err?.toString() ?? fallbackMessage, {
      title: i18n.t("workflowSchemaDialog.constants.notifications.invalidJSON", { ns: "workflows" }),
      severity: "error",
    })

    return false
  }
}

/**
 * Generates example data for schema file upload.
 *
 * @param value - The example value to use (default: "Value")
 * @returns Array of example objects with value and type
 */
export const getSchemaFileExample = (value = "Value"): { value: string; type: string }[] => {
  const exampleType = ["TEXT", "NUMBER", "DATE", "BOOLEAN", "LIST", "UNDEFINED"]

  const exampleData = Array.from([1, 2, 3, 4, 5, 6]).map((i) => {
    return {
      value: `${value} ${i}`,
      type: `${exampleType[i - 1]}`,
    }
  })

  return exampleData
}
